// src/context/ReviewContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { publicFetch, authFetch } from '@/services/api';
import { cache, CACHE_KEYS, CACHE_TTL } from '@/utils/cache';

/* ------------------------------------------------- */
/* REVIEW TYPES                                      */
/* ------------------------------------------------- */
export type Review = {
  reviewId: number;
  productId: number;
  productName: string;
  userId: number;
  userName: string;
  rating: number;
  reviewTitle?: string;
  comment?: string;
  imageUrls?: string[];
  isVerifiedPurchase: boolean;
  helpfulCount: number;
  createdDate: string;
  updatedDate?: string;
  canEdit: boolean;
};

export type ReviewSummary = {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    [key: string]: number;
  };
  fiveStarCount: number;
  fourStarCount: number;
  threeStarCount: number;
  twoStarCount: number;
  oneStarCount: number;
};

export type ReviewInput = {
  productId: number;
  rating: number;
  reviewTitle?: string;
  comment?: string;
  imageUrls?: string[];
  isVerifiedPurchase?: boolean;
};

/* ------------------------------------------------- */
/* CONTEXT TYPE                                      */
/* ------------------------------------------------- */
type ReviewContextType = {
  reviews: Review[];
  reviewSummary: ReviewSummary | null;
  loading: boolean;
  userHasReviewed: boolean;
  userReview: Review | null;
  fetchProductReviews: (productId: number) => Promise<void>;
  fetchReviewSummary: (productId: number) => Promise<void>;
  checkUserReviewed: (productId: number) => Promise<void>;
  submitReview: (reviewData: ReviewInput) => Promise<boolean>;
  markReviewHelpful: (reviewId: number) => Promise<void>;
  deleteReview: (reviewId: number) => Promise<boolean>;
  getUserReviews: () => Promise<Review[]>;
};

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

/* ------------------------------------------------- */
/* REVIEW PROVIDER                                   */
/* ------------------------------------------------- */
export const ReviewProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [userReview, setUserReview] = useState<Review | null>(null);
  const { user } = useAuth();

  // Add refs to track ongoing requests and prevent duplicates
  const fetchingProductReviews = useRef<number | null>(null);
  const fetchingSummary = useRef<number | null>(null);
  const checkingUserReview = useRef<number | null>(null);

  // -----------------------------------------------------------------
  // FETCH PRODUCT REVIEWS WITH CACHING
  // -----------------------------------------------------------------
  const fetchProductReviews = useCallback(async (productId: number) => {
    // Prevent duplicate requests for the same product
    if (fetchingProductReviews.current === productId) {
      return;
    }

    try {
      // Check cache first
      const cached = cache.get(
        CACHE_KEYS.PRODUCT_REVIEWS,
        { productId },
        { ttl: CACHE_TTL.REVIEWS }
      );
      
      if (cached) {
        console.log('🟢 Cache HIT: Product reviews');
        const reviewsArray = Array.isArray(cached) ? cached : [];
        setReviews(reviewsArray);
        
        // Check user review status
        if (user?.isAuthenticated && reviewsArray.length > 0) {
          const currentUserId = user.customerId || user.uid;
          if (currentUserId) {
            const userReviewFound = reviewsArray.find(review => 
              review.canEdit || 
              review.userId === currentUserId ||
              review.userId === parseInt(currentUserId) || 
              review.userId.toString() === currentUserId
            );
            setUserHasReviewed(!!userReviewFound);
            setUserReview(userReviewFound || null);
          }
        }
        return;
      }

      setLoading(true);
      fetchingProductReviews.current = productId;
      console.log('🔴 Cache MISS: Fetching reviews for product:', productId);
      
      const response = await publicFetch(`/api/reviews/product/${productId}`);
      if (response.ok) {
        const reviewsData = await response.json();
        console.log('ReviewContext: Reviews fetched:', reviewsData);
        const reviewsArray = Array.isArray(reviewsData) ? reviewsData : [];
        
        // Cache the reviews
        cache.set(
          CACHE_KEYS.PRODUCT_REVIEWS,
          reviewsArray,
          { productId },
          { ttl: CACHE_TTL.REVIEWS }
        );
        
        setReviews(reviewsArray);
        
        // Automatically check if current user has reviewed after fetching reviews
        if (user?.isAuthenticated && reviewsArray.length > 0) {
          const currentUserId = user.customerId || user.uid;
          if (currentUserId) {
            const userReviewFound = reviewsArray.find(review => 
              review.canEdit || 
              review.userId === currentUserId ||
              review.userId === parseInt(currentUserId) || 
              review.userId.toString() === currentUserId
            );
            setUserHasReviewed(!!userReviewFound);
            setUserReview(userReviewFound || null);
          }
        }
      } else {
        console.error('ReviewContext: Failed to fetch reviews:', response.status);
        setReviews([]);
      }
    } catch (error) {
      console.error('ReviewContext: Error fetching reviews:', error);
      setReviews([]);
    } finally {
      setLoading(false);
      fetchingProductReviews.current = null;
    }
  }, [user?.isAuthenticated, user?.customerId, user?.uid]);

  // -----------------------------------------------------------------
  // FETCH REVIEW SUMMARY WITH CACHING
  // -----------------------------------------------------------------
  const fetchReviewSummary = useCallback(async (productId: number) => {
    // Prevent duplicate requests for the same product
    if (fetchingSummary.current === productId) {
      return;
    }

    try {
      // Check cache first
      const cached = cache.get(
        CACHE_KEYS.REVIEW_SUMMARY,
        { productId },
        { ttl: CACHE_TTL.REVIEWS }
      );
      
      if (cached) {
        console.log('🟢 Cache HIT: Review summary');
        setReviewSummary(cached as ReviewSummary);
        return;
      }

      fetchingSummary.current = productId;
      console.log('🔴 Cache MISS: Fetching review summary for product:', productId);
      
      const response = await publicFetch(`/api/reviews/product/${productId}/summary`);
      if (response.ok) {
        const summaryData = await response.json();
        console.log('ReviewContext: Review summary fetched:', summaryData);
        
        // Cache the summary
        cache.set(
          CACHE_KEYS.REVIEW_SUMMARY,
          summaryData,
          { productId },
          { ttl: CACHE_TTL.REVIEWS }
        );
        
        setReviewSummary(summaryData);
      } else {
        console.error('ReviewContext: Failed to fetch review summary:', response.status);
        setReviewSummary(null);
      }
    } catch (error) {
      console.error('ReviewContext: Error fetching review summary:', error);
      setReviewSummary(null);
    } finally {
      fetchingSummary.current = null;
    }
  }, []);

  // -----------------------------------------------------------------
  // CHECK IF USER HAS REVIEWED PRODUCT (LOCAL CHECK ONLY)
  // -----------------------------------------------------------------
  const checkUserReviewed = useCallback(async (productId: number) => {
    // Prevent duplicate requests for the same product
    if (checkingUserReview.current === productId) {
      return;
    }

    if (!user?.isAuthenticated) {
      setUserHasReviewed(false);
      setUserReview(null);
      return;
    }

    try {
      checkingUserReview.current = productId;
      console.log('ReviewContext: Checking if user reviewed product locally:', productId);
      
      // Since backend doesn't have user-reviewed endpoint, check locally
      if (reviews.length > 0 && (user?.uid || user?.customerId)) {
        const currentUserId = user.customerId || user.uid;
        if (currentUserId) {
          const userReviewFound = reviews.find(review => 
            review.canEdit || 
            review.userId.toString() === currentUserId.toString() ||
            review.userId === parseInt(currentUserId.toString()) || // Handle string/number conversion
            review.userId.toString() === currentUserId.toString()
          );
          setUserHasReviewed(!!userReviewFound);
          setUserReview(userReviewFound || null);
          console.log('ReviewContext: User review check result:', {
            hasReviewed: !!userReviewFound,
            userReview: userReviewFound,
            currentUserId,
            reviewUserIds: reviews.map(r => r.userId)
          });
        } else {
          setUserHasReviewed(false);
          setUserReview(null);
        }
      } else {
        setUserHasReviewed(false);
        setUserReview(null);
      }
    } catch (error) {
      console.error('ReviewContext: Error checking user review:', error);
      setUserHasReviewed(false);
      setUserReview(null);
    } finally {
      checkingUserReview.current = null;
    }
  }, [user?.isAuthenticated, user?.uid, user?.customerId, reviews]);

  // -----------------------------------------------------------------
  // SUBMIT REVIEW WITH CACHE INVALIDATION
  // -----------------------------------------------------------------
  const submitReview = async (reviewData: ReviewInput): Promise<boolean> => {
    if (!user?.isAuthenticated) {
      throw new Error('Authentication required to submit review');
    }

    try {
      console.log('ReviewContext: Submitting review:', reviewData);
      
      const response = await authFetch('/api/reviews', {
        method: 'POST',
        body: JSON.stringify(reviewData),
      });

      if (response.ok) {
        const newReview = await response.json();
        console.log('ReviewContext: Review submitted successfully:', newReview);
        
        // Clear cache for this product's reviews
        cache.remove(CACHE_KEYS.PRODUCT_REVIEWS, { productId: reviewData.productId });
        cache.remove(CACHE_KEYS.REVIEW_SUMMARY, { productId: reviewData.productId });
        
        // Refresh reviews and summary
        await fetchProductReviews(reviewData.productId);
        await fetchReviewSummary(reviewData.productId);
        await checkUserReviewed(reviewData.productId);
        
        return true;
      } else {
        const errorText = await response.text();
        console.error('ReviewContext: Failed to submit review:', response.status, errorText);
        return false;
      }
    } catch (error) {
      console.error('ReviewContext: Error submitting review:', error);
      return false;
    }
  };

  // -----------------------------------------------------------------
  // MARK REVIEW AS HELPFUL
  // -----------------------------------------------------------------
  const markReviewHelpful = async (reviewId: number) => {
    try {
      console.log('ReviewContext: Marking review as helpful:', reviewId);
      
      const response = await authFetch(`/api/reviews/${reviewId}/helpful`, {
        method: 'POST',
      });

      if (response.ok) {
        // Update the helpful count in local state
        setReviews(prev => prev.map(review => 
          review.reviewId === reviewId 
            ? { ...review, helpfulCount: review.helpfulCount + 1 }
            : review
        ));
      } else {
        console.error('ReviewContext: Failed to mark review as helpful:', response.status);
      }
    } catch (error) {
      console.error('ReviewContext: Error marking review as helpful:', error);
    }
  };

  // -----------------------------------------------------------------
  // DELETE REVIEW
  // -----------------------------------------------------------------
  const deleteReview = async (reviewId: number): Promise<boolean> => {
    if (!user?.isAuthenticated) {
      throw new Error('Authentication required to delete review');
    }

    try {
      console.log('ReviewContext: Deleting review:', reviewId);
      
      const response = await authFetch(`/api/reviews/${reviewId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove review from local state
        setReviews(prev => prev.filter(review => review.reviewId !== reviewId));
        setUserHasReviewed(false);
        setUserReview(null);
        return true;
      } else {
        console.error('ReviewContext: Failed to delete review:', response.status);
        return false;
      }
    } catch (error) {
      console.error('ReviewContext: Error deleting review:', error);
      return false;
    }
  };

  // -----------------------------------------------------------------
  // GET USER'S REVIEWS (FROM LOCAL STATE - BACKEND ENDPOINT MISSING)
  // -----------------------------------------------------------------
  const getUserReviews = async (): Promise<Review[]> => {
    if (!user?.isAuthenticated) {
      return [];
    }

    try {
      console.log('ReviewContext: Getting user reviews from local state');
      
      // Since backend doesn't have my-reviews endpoint, filter from local reviews
      const currentUserId = user.customerId || user.uid;
      if (currentUserId) {
        const userReviews = reviews.filter(review => 
          review.canEdit || 
          review.userId.toString() === currentUserId.toString() ||
          review.userId === parseInt(currentUserId.toString()) || 
          review.userId.toString() === currentUserId.toString()
        );
        
        console.log('ReviewContext: User reviews found locally:', userReviews);
        return userReviews;
      }
      
      return [];
    } catch (error) {
      console.error('ReviewContext: Error getting user reviews:', error);
      return [];
    }
  };

  // -----------------------------------------------------------------
  // PROVIDE CONTEXT
  // -----------------------------------------------------------------
  const contextValue = useMemo(() => ({
    reviews,
    reviewSummary,
    loading,
    userHasReviewed,
    userReview,
    fetchProductReviews,
    fetchReviewSummary,
    checkUserReviewed,
    submitReview,
    markReviewHelpful,
    deleteReview,
    getUserReviews,
  }), [
    reviews,
    reviewSummary,
    loading,
    userHasReviewed,
    userReview,
    fetchProductReviews,
    fetchReviewSummary,
    checkUserReviewed,
  ]);

  return (
    <ReviewContext.Provider value={contextValue}>
      {children}
    </ReviewContext.Provider>
  );
};

/* ------------------------------------------------- */
/* USE REVIEW HOOK                                   */
/* ------------------------------------------------- */
export const useReview = (): ReviewContextType => {
  const ctx = useContext(ReviewContext);
  if (!ctx) {
    throw new Error('useReview must be used within a ReviewProvider');
  }
  return ctx;
};